import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import useUpdate from '../../api/useUpdate';
import useFetchObject from '../../api/useFetchObject';
import LoadingScreen from '../../components/common/LoadingScreen';

const MinistryEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: ministry, isLoading } = useFetchObject('ministries', 'ministries', id);
  const { handleUpdate, loading, success } = useUpdate('ministries');
  const [submitting, setSubmitting] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required')),
    code: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    is_active: Yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await handleUpdate(id, values);
      toast.success(t('ministries.update_success'));
      navigate('/ministries');
    } catch (error) {
      console.error('Error updating ministry:', error);
      toast.error(error.response?.data?.message || t('common.error_updating'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!ministry) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          {t('ministries.not_found')}
        </Typography>
        <Button
          component={RouterLink}
          to="/ministries"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          {t('common.back_to_list')}
        </Button>
      </Container>
    );
  }

  const initialValues = {
    name: ministry.name || '',
    name_dari: ministry.name_dari || '',
    name_pashto: ministry.name_pashto || '',
    code: ministry.code || '',
    description: ministry.description || '',
    is_active: ministry.is_active !== undefined ? ministry.is_active : true,
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/ministries"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('ministries.edit_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('ministries.edit_description')}
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          values,
          handleChange,
          setFieldValue,
        }) => (
          <Form>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('ministries.basic_info')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="name"
                      label={t('ministries.name')}
                      fullWidth
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="code"
                      label={t('ministries.code')}
                      fullWidth
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code && errors.code}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="description"
                      label={t('ministries.description')}
                      fullWidth
                      multiline
                      rows={3}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('common.multilingual_information')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="name_dari"
                      label={t('ministries.name_dari')}
                      fullWidth
                      dir="rtl"
                      inputProps={{ lang: 'fa' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="name_pashto"
                      label={t('ministries.name_pashto')}
                      fullWidth
                      dir="rtl"
                      inputProps={{ lang: 'ps' }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('common.status')}
                </Typography>
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.is_active}
                        onChange={(e) => setFieldValue('is_active', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={values.is_active ? t('common.active') : t('common.inactive')}
                  />
                  <FormHelperText>
                    {values.is_active
                      ? t('ministries.active_help')
                      : t('ministries.inactive_help')}
                  </FormHelperText>
                </FormControl>
              </CardContent>
            </Card>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate('/ministries')}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {t('common.save')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default MinistryEdit; 